import { ADD_ON_IDS, payflowTrackActionsService } from '@ctct/domain-layer';

import type { Controller, Listeners, TEventSource } from '../helpers';
import { controllerHelpers } from '../helpers';
import {
  addOnsEventBus,
  contactsTierSelectEventBus,
  currencyEventBus,
  emailPlansEventBus,
  nonProfitEventBus,
  orderSummaryEventBus,
  payflowTopBarEventBus,
  paymentButtonEventBus,
  prepaySelectEventBus,
  promoCodesEventBus,
  thankYouEventBus,
  type ContactsTierSelectManager,
  type EmailPlanChangedPayload,
  type EmailPlansManager,
  type NonProfitManager,
  type PrepaySelectManager,
} from '../managers';

export class PayflowTrackActionsController implements Controller {
  public listeners: Listeners;

  constructor(
    private emailPlansManager: EmailPlansManager,
    private contactsTierManager: ContactsTierSelectManager,
    private nonProfitManager: NonProfitManager,
    private prepayManager: PrepaySelectManager
  ) {
    const eventConnections = [
      emailPlansEventBus.bindTo('emailPlanChanged', this.onEmailPlanChanged),
      emailPlansEventBus.bindTo('smsBundleRemoved', this.onSmsRemovedFromPlanSelection),
      addOnsEventBus.bindTo('addOnAdded', this.onAddOnsAdded),
      addOnsEventBus.bindTo('addOnRemoved', this.onAddOnsRemoved),
      orderSummaryEventBus.bindTo('addOnRemoved', this.onSmsRemovedFromCheckout),

      prepaySelectEventBus.bindTo('prepayChanged', this.onPrepayChanged),
      currencyEventBus.bindTo('currencyHasChanged', this.onCurrencyChanged),
      nonProfitEventBus.bindTo('nonProfitChanged', this.onNonProfitChanged),
      contactsTierSelectEventBus.bindTo('contactTierChanged', this.onContactsTierChanged),

      promoCodesEventBus.bindTo('promoCodeApplySuccess', this.onPromoCodeApplied),
      promoCodesEventBus.bindTo('promoCodeRemove', this.onPromoCodeRemoved),

      paymentButtonEventBus.bindTo('payForPlanClicked', this.onPayForPlanClicked),
      paymentButtonEventBus.bindTo('payForPlanSuccess', this.onPayForPlanSuccess),
      paymentButtonEventBus.bindTo('payForPlanError', this.onPayForPlanError),

      thankYouEventBus.bindTo('xeroPartnerButtonClicked', this.onXeroPartnerButtonClicked),

      payflowTopBarEventBus.bindTo('exitClicked', this.onExitClicked),
    ];

    this.listeners = controllerHelpers.createListeners(eventConnections);
  }

  private onEmailPlanChanged = async ({ planName }: EmailPlanChangedPayload, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    const [contactsTier, nonProfit, prepay] = await Promise.all([
      this.contactsTierManager.getApiWhenReady(),
      this.nonProfitManager.getApiWhenReady(),
      this.prepayManager.getApiWhenReady(),
    ]);

    payflowTrackActionsService.payflowPlanSelected({
      planName,
      contacts: parseInt(contactsTier.getContactTier(), 10),
      prepayValue: prepay.getPrepay(),
      nonProfit: nonProfit.getIsNonProfit(),
      fromComparisonTable: false,
    });
  };

  private onAddOnsAdded = (addOnId: string, _option: undefined | string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.addOnSelected(addOnId);
  };

  private onAddOnsRemoved = (addOnId: string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.addOnRemoved(addOnId);
  };

  private onSmsRemovedFromPlanSelection = ({ source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.smsBundleRemoved('plan-selection');
  };

  private onSmsRemovedFromCheckout = (addOnId: string, { source }: { source: TEventSource }) => {
    if (source !== 'user' || addOnId !== ADD_ON_IDS.SMS) {
      return;
    }

    payflowTrackActionsService.smsBundleRemoved('checkout');
  };

  private onPrepayChanged = (prepayValue: string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.prepaySelected(prepayValue);
  };

  private onCurrencyChanged = (currency: string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.currencySelected(currency);
  };

  private onNonProfitChanged = (isNonProfit: boolean, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.nonProfitSelected(isNonProfit);
  };

  private onContactsTierChanged = (contactsTier: string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.contactsAmountSelected(parseInt(contactsTier, 10));
  };

  private onPromoCodeApplied = (_code: string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.discountCodeApplied();
  };

  private onPromoCodeRemoved = (_code: string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.discountCodeRemoved();
  };

  // payment processing
  private onPayForPlanClicked = async (_, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    const emailPlansApi = await this.emailPlansManager.getApiWhenReady();

    if (emailPlansApi.getIsTrialer()) {
      payflowTrackActionsService.submitPayment();
    } else {
      payflowTrackActionsService.submitUpgrade();
    }
  };

  private onPayForPlanSuccess = async (_isSuccess: boolean, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    const emailPlansApi = await this.emailPlansManager.getApiWhenReady();

    if (emailPlansApi.getIsTrialer()) {
      payflowTrackActionsService.paymentSuccess();
    } else {
      payflowTrackActionsService.upgradeSuccess();
    }
  };

  private onPayForPlanError = async (_errorMessage: string, { source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    const emailPlansApi = await this.emailPlansManager.getApiWhenReady();

    if (emailPlansApi.getIsTrialer()) {
      payflowTrackActionsService.paymentError();
    } else {
      payflowTrackActionsService.upgradeError();
    }
  };

  private onXeroPartnerButtonClicked = ({ source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.thankYouPageXeroPartnerButtonClicked();
  };

  private onExitClicked = ({ source }: { source: TEventSource }) => {
    if (source !== 'user') {
      return;
    }

    payflowTrackActionsService.bounce();
  };
}
